import { useEffect, useState, useRef } from 'react';
import { hysteriFetch, wrappers } from 'hysterics';
import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from 'ddc-new-relic';
import transformFFSSResponse from '../utils/transformFFSSResponse';
import {
	getFilterParams,
	sanitizeListingConfigId
} from '../utils/submitSearchUtils';

const suggestionsFetchWrapper = hysteriFetch(
	'ws-inv-text-search-suggestions-proxy-fetch'
);

// TODO use Suspense instead of hook to fetch data, when Suspense is ready
// https://reactjs.org/docs/concurrent-mode-suspense.html
const useFreeFormSearchServiceApi = (
	input,
	contextEnabled,
	siteId,
	newSuggestionsEndpointFlag
) => {
	const apiURL = newSuggestionsEndpointFlag
		? '/api/widget/ws-inv-text-search/getSuggestions'
		: window.DDC.InvData.SEARCH_API_URL;
	const { origin } = window.location;
	const [response, setResponse] = useState(null);
	const [fetchError, setError] = useState(null);
	const requestCounterRef = useRef(0);

	useEffect(() => {
		if (input) {
			// Reset response and error when input changes
			setResponse(null);
			setError(null);

			if (newSuggestionsEndpointFlag) {
				// Increment request counter and capture it for this request
				requestCounterRef.current += 1;
				const currentRequestId = requestCounterRef.current;

				(async () => {
					const listingConfigIdPref =
						window.DDC?.InvData?.prefs?.['listing.config.id'] || '';
					const listingConfigId =
						sanitizeListingConfigId(listingConfigIdPref);
					const queryString = `?searchTerm=${encodeURIComponent(
						input
					)}&siteId=${encodeURIComponent(
						siteId
					)}&listingConfigId=${encodeURIComponent(listingConfigId)}`;
					const searchUrl = `${apiURL}${queryString}`;
					try {
						const searchResults = await suggestionsFetchWrapper(
							searchUrl,
							{
								method: 'GET'
							},
							{
								[wrappers.timeout.contextKey]: {
									timeout: 2500
								},
								[wrappers.retry.contextKey]: {
									retries: 0
								}
							}
						);

						// Only update state if this is still the latest request
						if (currentRequestId === requestCounterRef.current) {
							setNewRelicCustomAttribute(
								'wsInvTextSearchInput',
								input
							);
							setNewRelicCustomAttribute(
								'wsInvTextSearchInputNumResults',
								searchResults?.suggestions?.[0]?.count > 0
									? searchResults.suggestions.length
									: 0
							);
							setResponse(searchResults);
							setError(null);
						}
					} catch (err) {
						// Only update error if this is still the latest request
						if (currentRequestId === requestCounterRef.current) {
							setError(err);
							setResponse({}); // Set empty response so fetch is considered complete
						}
					}
				})();
			} else {
				// Increment request counter and capture it for this request
				requestCounterRef.current += 1;
				const currentRequestId = requestCounterRef.current;

				(async () => {
					let filterParams = '';
					if (contextEnabled) {
						filterParams = getFilterParams(
							window.DDC.InvData.getAppliedFilters()
						);
						if (filterParams) {
							filterParams = filterParams.concat('&');
						}
					}
					const search = `search=${encodeURIComponent(input)}`;
					const searchUrl = `${origin}${apiURL}?${filterParams}${search}`;
					const startTime = Date.now();
					try {
						const searchResults = await fetch(searchUrl);
						setNewRelicCustomAttribute(
							'wsInvTextSearchInput',
							input
						);
						if (searchResults.ok) {
							const result = await searchResults.json();

							// Only update state if this is still the latest request
							if (
								currentRequestId === requestCounterRef.current
							) {
								addNewRelicPageAction(
									'WS INV TEXT SEARCH FETCH SUCCESS',
									{
										requestUrl: searchUrl,
										requestDuration: Date.now() - startTime,
										responseLength:
											JSON.stringify(result).length,
										responseStatus:
											searchResults.statusText,
										statusCode: searchResults.status
									}
								);
								setNewRelicCustomAttribute(
									'wsInvTextSearchInputNumResults',
									result.suggestions[0].count > 0
										? result.suggestions.length
										: 0
								);
								setResponse(transformFFSSResponse(result));
								setError(null);
							}
						} else {
							throw Object.assign(
								new Error('Error fetching search suggestions'),
								{
									responseStatus: searchResults.statusText,
									statusCode: searchResults.status
								}
							);
						}
					} catch (err) {
						// Only update error if this is still the latest request
						if (currentRequestId === requestCounterRef.current) {
							addNewRelicPageAction(
								'WS INV TEXT SEARCH FETCH ERROR',
								{
									requestUrl: searchUrl,
									requestDuration: Date.now() - startTime,
									responseStatus: err.responseStatus,
									statusCode: err.statusCode
								}
							);
							setError(err);
							setResponse({}); // Set empty response so fetch is considered complete
						}
					}
				})();
			}
		} else {
			setResponse(null);
		}
	}, [
		apiURL,
		origin,
		input,
		contextEnabled,
		siteId,
		newSuggestionsEndpointFlag
	]);
	return [response, fetchError];
};

export default useFreeFormSearchServiceApi;
